import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { usePreviewSeekAd } from '../../SeekAd/hooks/usePreviewSeekAd';
import { withScoutTheme } from '../../ThemeContext/ThemeContext';
import { Step1, Step2, Step3, Step4 } from './Steps';
import { Button, Dialog, Snackbar, CircularProgress } from '@mui/material';
import useStyles from './useStyles';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import axios from 'axios';
import ExitConfirmationDialog from './Components/ExitConfirmationDialog';
import StepperComponent from '../../../components/NewUI/Components/CustomUIElements/StepperComponent';
import JobAdSuccessDialog from '../../../components/NewUI/Components/CustomUIElements/JobAdSuccessDialog';

const steps = [
  { label: 'Classify Job', value: 'classify_job' },
  { label: 'What Jobseekers See In Search Results', value: 'search_results' },
  { label: 'Ad Description', value: 'description' },
  { label: 'Preview', value: 'preview' },
  { label: 'Publish', value: 'publish' }
];

function AdEditor({
  jobAdId,
  apiKey,
  jobId,
  linkOutEnabled,
  privacyOverLimit,
  seek_billing_reference,
  seek_billing_reference_flag,
  job_url_prop
}) {
  const [saving, setSaving] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [isSnackOpen, setIsSnackOpen] = useState(false);
  const [brandingEnabled, setBrandingEnabled] = useState(false);
  const [maxBulletPoints, setMaxBulletPoints] = useState(0);
  const [snackMessage, setSnackMessage] = useState('');
  const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState(false);
  const [failedSeekDialogOpen, setFailedSeekDialogOpen] = useState(false);
  const [failedSeekDialogMessage, setfailedSeekDialogMessage] = useState('');
  const [activeStep, setActiveStep] = useState(0);
  const [isExitModalOpen, setIsExitModalOpen] = useState(false);
  const [getSeekAdBrandingData, setGetSeekAdBrandingData] = useState(null);
  const [answers, setAnswers] = useState(null);
  const [activeError, setActiveError] = useState(null);
  const [activeErrorMessage, setActiveErrorMessage] = useState(null);
  const [editExistingJob, setEditExistingJob] = useState(false);

  const classes = useStyles();
  const { previewUrl } = usePreviewSeekAd(jobAdId);

  async function submit(type) {
    try {
      type === 'draft' ? setSavingDraft(true) : setSaving(true);
      const { data } = await axios.put(
        `/api/v4/seek/job_ads/${jobAdId}`,
        { job_ad: answers, draft: type === 'draft' ? true : false },
        {
          headers: { 'X-api-authenticate': apiKey }
        }
      );
      setAnswers(data);
      if (activeStep === 3 && type !== 'draft') {
        try {
          data.advertisement_id
            ? await axios.put(`/api/v4/seek/job_ads/${jobAdId}/ad_posting`, null, {
                headers: { 'X-api-authenticate': apiKey }
              })
            : await axios.post(`/api/v4/seek/job_ads/${jobAdId}/ad_posting`, null, {
                headers: { 'X-api-authenticate': apiKey }
              });
          setIsSuccessDialogOpen(true);
        } catch (e) {
          setFailedSeekDialogOpen(true);
          return;
        }
      }
      if (type === 'draft') {
        handleSnack('Draft saved');
        setIsExitModalOpen(false);
        window.location.href = `/admin/jobs/${jobId}/job_ads`;
      } else if (type === 'submit') {
        setIsSuccessDialogOpen(true);
      } else if (type === 'next') {
        setActiveStep(activeStep + 1);
      }
    } catch (e) {
      if (type === 'draft') {
        handleSnack('Unable to save draft');
      } else if (type === 'submit') {
        handleSnack('Unable to post to SEEK. Please check required fields and try again.');
      } else if (type === 'next') {
        setActiveStep(activeStep + 1);
      }
    } finally {
      setSaving(false);
      setSavingDraft(false);
    }
  }

  function handleSnack(message) {
    setIsSnackOpen(true);
    setSnackMessage(message);
  }

  async function getJobData() {
    try {
      const { data } = await axios.get(`/api/v4/seek/job_ads/${jobAdId}`, {
        headers: { 'X-api-authenticate': apiKey }
      });
      setAnswers(data);
      setEditExistingJob(data.advertisement_id && data.advertisement_published_at ? true : false);
      setActiveStep(0);
    } catch (e) {
      // Handle error silently
    }
  }

  useEffect(() => {
    getJobData();
  }, []);

  useEffect(() => {
    setActiveError(null);
  }, [answers]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeStep]);

  const requiredRefs = {
    title: useRef(null),
    listing_location: useRef(null),
    category: useRef(null),
    salary: useRef(null),
    description: useRef(null),
    ad_details: useRef(null),
    link_out: useRef(null),
    video_embed_code: useRef(null),
    ad_type: useRef(null)
  };

  function setError(data) {
    setActiveError(requiredRefs[Object.keys(data)[0]]);
    setActiveErrorMessage(Object.values(data)[0]);
  }

  async function handleNext(type) {
    const errorRef = checkErrors();
    if (errorRef) {
      const ref = requiredRefs[errorRef];
      setActiveError(ref);
      window.scrollTo({ top: ref.current.offsetTop - 120, behavior: 'smooth' });
    } else {
      try {
        const { data } = await axios.post(
          `/api/v4/seek/job_ads/${jobAdId}/validate`,
          { job_ad: { ...answers, current_step: steps[activeStep].value } },
          {
            headers: { 'X-api-authenticate': apiKey }
          }
        );
        if (data.success) {
          submit(type);
          if (activeError) {
            setActiveError(null);
            setActiveErrorMessage(null);
          }
        } else {
          setFailedSeekDialogOpen(true);
          setfailedSeekDialogMessage(data.error_message);
        }
      } catch (e) {
        return e;
      }
    }
  }

  function checkErrors() {
    const error = Object.keys(requiredRefs).find((ref) => {
      if (activeStep === 0) {
        if (['title', 'listing_location'].includes(ref)) {
          return !answers[ref] && ref;
        } else if (ref === 'category') {
          return answers.use_suggested_classification
            ? !answers.listing_sub_classification && ref
            : !answers.listing_sub_classification && !answers.listing_classification && ref;
        } else if (ref === 'salary') {
          let difference = answers.salary_max - answers.salary_min;
          return (
            !answers.salary_min ||
            !answers.salary_max ||
            (answers.salary_min < 100000 && difference > 100000 && ref)
          );
        } else if (ref === 'ad_type') {
          return !answers[ref] && ref;
        }
      } else if (activeStep === 1) {
        if (['description'].includes(ref)) {
          return !answers[ref] && ref;
        } else if (ref === 'video_embed_code' && answers.video_embed_code !== '') {
          if (!answers.video_embed_code) return;
          const regex =
            /https:\/\/((www\.youtube\.com\/embed|youtu\.be)\/[\w-]+(\?[\w-]+=[\w-]*(&[\w-]+=[\w-]*)*)?|www\.youtube\.com\/watch\?v=[\w-]+(&[\w-]+=[\w-]*)*)/;
          return !answers.video_embed_code.match(regex) && ref;
        }
      } else if (activeStep === 2) {
        if (['title', 'ad_details'].includes(ref)) {
          return !answers[ref] && ref;
        }
      }
    });
    return error;
  }

  const buttonName = () => {
    if (activeStep + 2 >= steps.length) {
      return editExistingJob ? 'Update' : 'Publish';
    } else {
      return 'Next';
    }
  };

  return (
    <div className={classes.containerOuter}>
      <div className={classes.container}>
        <StepperComponent activeStep={activeStep} steps={steps} classes={classes} />
        {answers && activeStep === 0 && (
          <Step1
            answers={answers}
            setAnswers={setAnswers}
            apiKey={apiKey}
            jobId={jobId}
            jobAdId={jobAdId}
            requiredRefs={requiredRefs}
            activeError={activeError}
            setBrandingEnabled={setBrandingEnabled}
            setMaxBulletPoints={setMaxBulletPoints}
            seek_billing_reference={seek_billing_reference}
            seek_billing_reference_flag={seek_billing_reference_flag}
          />
        )}
        {answers && activeStep === 1 && (
          <Step2
            answers={answers}
            setAnswers={setAnswers}
            getSeekAdBrandingData={getSeekAdBrandingData}
            setGetSeekAdBrandingData={setGetSeekAdBrandingData}
            apiKey={apiKey}
            jobAdId={jobAdId}
            requiredRefs={requiredRefs}
            activeError={activeError}
            activeErrorMessage={activeErrorMessage}
            maxBulletPoints={maxBulletPoints}
            brandingEnabled={brandingEnabled}
          />
        )}
        {answers && activeStep === 2 && (
          <Step3
            setActiveStep={setActiveStep}
            answers={answers}
            setAnswers={setAnswers}
            requiredRefs={requiredRefs}
            activeError={activeError}
            linkOutEnabled={linkOutEnabled}
            privacyOverLimit={privacyOverLimit}
            activeErrorMessage={activeErrorMessage}
            jobId={jobId}
            setActiveError={setActiveError}
            setActiveErrorMessage={setActiveErrorMessage}
            job_url_prop={job_url_prop}
          />
        )}
        {answers && (activeStep === 3 || activeStep === 4) && <Step4 answers={answers} />}
        <div className={classes.buttonContainer}>
          <a onClick={() => setIsExitModalOpen(true)}>Save and exit</a>
          <div className={classes.buttons}>
            {activeStep !== 0 && (
              <Button
                disableElevation
                className={classes.buttonSecondaryLight}
                onClick={() => {
                  setAnswers({ ...answers, current_step: steps[activeStep - 1].value });
                  setActiveStep(activeStep - 1);
                }}
              >
                <ChevronLeftIcon />
              </Button>
            )}
            {activeStep === 3 && (
              <Button
                disableElevation
                className={`${classes.buttonSecondary} preview`}
                style={{ minWidth: 130 }}
                href={previewUrl}
                target="_blank"
              >
                Preview on SEEK
              </Button>
            )}
            {activeStep !== 4 && (
              <Button
                disableElevation
                className={classes.buttonSecondary}
                id="SeekNextStepMultiposterBtn"
                style={{ minWidth: 130 }}
                onClick={() => handleNext(activeStep + 1 === steps.length ? 'submit' : 'next')}
                endIcon={saving ? null : <ChevronRightIcon />}
              >
                {saving ? <CircularProgress size={20} color="inherit" /> : buttonName()}
              </Button>
            )}
          </div>
        </div>
      </div>
      <ExitConfirmationDialog
        saving={saving}
        savingDraft={savingDraft}
        setActiveStep={setActiveStep}
        isExitModalOpen={isExitModalOpen}
        setIsExitModalOpen={setIsExitModalOpen}
        editExistingJob={editExistingJob}
        submit={submit}
      />
      <JobAdSuccessDialog
        open={isSuccessDialogOpen}
        onClose={() => window.location.replace(document.referrer)}
        answers={answers}
        classes={classes}
        adType={'seek'}
        jobId={jobId}
      />
      <Dialog
        open={failedSeekDialogOpen}
        onClose={() => {
          setFailedSeekDialogOpen(false);
          setfailedSeekDialogMessage('');
        }}
        classes={{ paper: classes.failDialog }}
      >
        <h4>Posting Failed</h4>
        <span>
          {failedSeekDialogMessage || 'Please check all your details again and republish.'}
        </span>
        <div className={classes.dialogButtonContainer}>
          <Button
            disableElevation
            className={classes.buttonSecondary}
            onClick={() => {
              setFailedSeekDialogOpen(false);
              setfailedSeekDialogMessage('');
              setActiveStep(0);
            }}
          >
            <span style={{ marginRight: '0.5rem' }}>Ok</span> &gt;
          </Button>
        </div>
      </Dialog>
      <Snackbar open={isSnackOpen} onClose={() => setIsSnackOpen(false)} message={snackMessage} />
    </div>
  );
}

AdEditor.propTypes = {
  jobAdId: PropTypes.number.isRequired,
  jobId: PropTypes.number.isRequired,
  apiKey: PropTypes.string.isRequired,
  linkOutEnabled: PropTypes.bool.isRequired,
  privacyOverLimit: PropTypes.bool,
  seek_billing_reference: PropTypes.bool,
  seek_billing_reference_flag: PropTypes.bool,
  job_url_prop: PropTypes.string
};

export default withScoutTheme(AdEditor);
